import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { ArrowDown, CheckCircle2, Phone } from "lucide-react";
import { Container } from "@/components/site/Container";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CalculatorQuiz } from "@/components/calculator/CalculatorQuiz";
import { canonical } from "@/lib/seo";
import type { RoofType } from "@/lib/roofCostEstimator";

const SLUG = "/roof-cost-estimate";
const TITLE = "Instant Roof Cost Estimate | Durafoam Roofing Phoenix";
const DESCRIPTION =
  "Get a ballpark price for a foam, tile, or shingle roof in Phoenix in about a minute. Answer a few questions and see your estimated range — then book a free inspection.";

const searchSchema = z.object({
  roofType: z.enum(["foam", "tile", "shingle"]).optional(),
});

export const Route = createFileRoute("/roof-cost-estimate")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: canonical(SLUG) }],
  }),
  component: RoofCostEstimatePage,
});

function RoofCostEstimatePage() {
  const { roofType } = Route.useSearch();
  return (
    <>
      <Breadcrumbs items={[{ href: SLUG, label: "Instant Roof Cost Estimate" }]} />

      <section className="bg-primary py-6 text-primary-foreground sm:py-8">
        <Container className="max-w-5xl text-center">
          <h1 className="text-3xl font-semibold tracking-tight !text-primary-foreground sm:text-4xl">Instant Roof Quote</h1>
          <p className="mt-2 flex flex-col items-center gap-1 text-lg font-medium text-primary-foreground/85 sm:text-xl">
            Start now!
            <ArrowDown className="h-8 w-8 animate-bounce text-accent" aria-hidden="true" />
          </p>
        </Container>
      </section>


      <CalculatorQuiz initialRoofType={roofType as RoofType | undefined} />

      <section className="border-t border-border bg-muted/40 py-12">
        <Container className="max-w-3xl">
          <h2 className="text-2xl font-bold text-foreground">How accurate is this estimate?</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
            The ranges come from the same per-square-foot pricing we use on real Phoenix jobs, adjusted
            for roof size. What moves the final number is what we find on the roof: existing layers,
            drainage, parapet walls, penetrations, and how much repair the deck needs before coating.
            That's what the free inspection is for. Licensed ROC223900 and roofing the Valley since
            1989.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href="tel:+14809415373"
              className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-lg bg-accent px-6 text-base font-semibold text-primary hover:bg-[var(--accent-hover)]"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> Call 480-941-5373
            </a>
            <Link
              to="/roofing-costs-phoenix"
              className="inline-flex min-h-[52px] items-center justify-center rounded-lg border border-border bg-card px-6 text-base font-semibold text-foreground hover:border-accent"
            >
              See full Phoenix roofing cost guide
            </Link>
          </div>
        </Container>
      </section>
    </>
  );
}
